import { useCallback, useMemo } from "react";

import type {
  DispatchViewStatePatch,
  ResolvedDispatchPageSettings,
  ResolvedDispatchView,
} from "@storyboard/dispatch-view/resolve";
import { resolveDispatchPageSettings, resolveDispatchView } from "@storyboard/dispatch-view/resolve";
import type { DispatchAction, DispatchViewDefinition, DispatchViewState } from "@storyboard/dispatch-view/view";
import { allDispatchColumns, type OfferedDispatchFields, offeredDispatchFields } from "@/components/Dispatch/columns";
import { useWebSettings } from "@/models/StateProviders/WebSettingsProvider";
import type { TagDefinition } from "@/types/driver";
import { useDispatchView } from "./useDispatchView";
import { useDispatchViewState } from "./useDispatchViewState";

/** The actions withheld while the workspace's task surface is off. */
const TASKS_WITHHELD: ReadonlySet<DispatchAction> = new Set(["task"]);

/** Nothing withheld: the task surface is on. */
const NOTHING_WITHHELD: ReadonlySet<DispatchAction> = new Set();

/** The page's settings in force and the switch a member flips from the page. */
export type DispatchPageControls = ResolvedDispatchPageSettings & {
  setShowShortcuts: (shown: boolean) => void;
};

/**
 * The view the page renders from, already reconciled: the active definition (from `?view=`), the member's state
 * over it, and the page's settings, resolved once through `@storyboard/dispatch-view/resolve` so no call site lays
 * state over a definition by hand.
 *
 * `resolved` is `null` while the cascade offers no view at all. `state` is the compiled entry the view was resolved
 * with, for a control that shows what the member chose rather than what changed. `write` and `reset` address the
 * active view's state entry with a sparse patch, and the chips read what the compiled state changes about the view,
 * whoever wrote it. `page` carries the keymap, the shortcut bar switch, and the phone tag's slug; the task action is
 * left unbound while the workspace's task surface is off. `offered` is what the pickers, menus, and filters may offer:
 * while tasks are off, no task field and no `task` item kind.
 *
 * @param tags - The workspace's tags the member may show as columns; the offered column set is the view's columns
 *   first, then the other driver fields, then these by label.
 */
export function useResolvedDispatchView(tags: readonly TagDefinition[]): {
  definition: DispatchViewDefinition | null;
  views: DispatchViewDefinition[];
  selectView: (viewId: string) => void;
  resolved: ResolvedDispatchView | null;
  state: DispatchViewState | undefined;
  write: (patch: DispatchViewStatePatch) => void;
  reset: () => void;
  page: DispatchPageControls;
  offered: OfferedDispatchFields;
} {
  const { settings, updateWebSettings } = useWebSettings();
  const { view: definition, views, selectView } = useDispatchView();
  const { state, write, reset } = useDispatchViewState(definition?.id);

  const { keymap, showShortcuts, phoneTagSlug, tasks } = settings.dispatch;
  const tagsBySlug = useMemo(() => new Map(tags.map((tag) => [tag.slug, tag])), [tags]);
  const offered = useMemo(() => offeredDispatchFields(tasks, tagsBySlug), [tasks, tagsBySlug]);
  const resolved = useMemo(
    () =>
      definition
        ? resolveDispatchView(definition, state, allDispatchColumns(definition, tags, tagsBySlug, offered.coreKinds))
        : null,
    [definition, state, tags, tagsBySlug, offered.coreKinds],
  );

  // With tasks off the task action is withheld, so its key fires nothing and the shortcut bar lists what it did before.
  const pageSettings = useMemo(
    () =>
      resolveDispatchPageSettings(
        { keymap, showShortcuts, phoneTagSlug: phoneTagSlug ?? undefined },
        tasks ? NOTHING_WITHHELD : TASKS_WITHHELD,
      ),
    [keymap, showShortcuts, phoneTagSlug, tasks],
  );
  const setShowShortcuts = useCallback(
    (shown: boolean) => updateWebSettings({ dispatch: { showShortcuts: shown } }),
    [updateWebSettings],
  );
  const page = useMemo<DispatchPageControls>(
    () => ({ ...pageSettings, setShowShortcuts }),
    [pageSettings, setShowShortcuts],
  );

  return { definition, views, selectView, resolved, state, write, reset, page, offered };
}
